import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BookOpen, ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { Button } from "../../components/Button";
import { Label } from "../../components/Label";
import { CustomTextField } from "../../components/CustomTextField";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import SuccessPopup from "../../components/SuccessPopup";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { normalizeLinks } from "../../utils/linkLists";

const DOCUMENTS_ENDPOINT = "agencies/documents/";

const EMPTY_FORM = { name: "", url: "" };

const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  const first = Object.values(data)[0];
  return (Array.isArray(first) ? first[0] : first) || fallback;
};

function DocumentFormModal({ open, document, onClose, onSaved }) {
  const theme = useTheme();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const neutralBorderColor =
    theme.palette.border?.input || theme.palette.divider || "#d0d5dd";

  useEffect(() => {
    if (open) {
      setForm(document ? { name: document.name, url: document.url } : EMPTY_FORM);
      setError("");
    }
  }, [open, document]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { name: form.name.trim(), url: normalizeLinks([form.url])[0] };

    setIsSubmitting(true);
    try {
      if (document) {
        await apiClient.patch(`${DOCUMENTS_ENDPOINT}${document.id}/`, payload);
      } else {
        await apiClient.post(DOCUMENTS_ENDPOINT, payload);
      }
      onSaved(document ? "Documento actualizado" : "Documento añadido");
    } catch (err) {
      setError(getErrorMessage(err, "No se pudo guardar el documento"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      title={document ? "Editar documento" : "Nuevo documento"}
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Box>
            <Label sx={{ fontSize: "1rem" }}>Nombre *</Label>
            <CustomTextField
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Ej: Manual de onboarding"
              autoFocus
              disabled={isSubmitting}
              focusBorderColor={neutralBorderColor}
              sx={{ marginTop: "8px" }}
            />
          </Box>
          <Box>
            <Label sx={{ fontSize: "1rem" }}>Enlace *</Label>
            <CustomTextField
              value={form.url}
              onChange={handleChange("url")}
              placeholder="https://..."
              disabled={isSubmitting}
              error={!!error}
              errorMessage={error}
              focusBorderColor={neutralBorderColor}
              sx={{ marginTop: "8px" }}
            />
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ pt: 1 }}>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !form.name.trim() || !form.url.trim()}
            >
              {isSubmitting ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </UnifiedModal>
  );
}

export function Documentation() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { interceptorReady } = useAuth();
  const { setPageContext } = useOutletContext();
  const [search, setSearch] = useState("");
  const [formState, setFormState] = useState({ open: false, document: null });
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", isError: false });

  const { data, isLoading } = getData(DOCUMENTS_ENDPOINT, interceptorReady);

  const documents = useMemo(() => {
    const list = Array.isArray(data) ? data : data?.results || [];
    const term = search.trim().toLowerCase();
    return term ? list.filter((doc) => doc.name.toLowerCase().includes(term)) : list;
  }, [data, search]);

  useEffect(() => {
    setPageContext({
      title: "Documentación",
      subtitle: "Enlaces a la documentación interna de la agencia",
      showTitle: true,
      rightContent: (
        <Button onClick={() => setFormState({ open: true, document: null })} sx={{ gap: 1 }}>
          <Plus size={16} />
          Añadir documento
        </Button>
      ),
    });
  }, [setPageContext]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["getData", DOCUMENTS_ENDPOINT] });

  const handleSaved = (message) => {
    setFormState({ open: false, document: null });
    setNotification({ open: true, message, isError: false });
    refresh();
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`${DOCUMENTS_ENDPOINT}${documentToDelete.id}/`);
      setNotification({ open: true, message: "Documento eliminado", isError: false });
      refresh();
    } catch (err) {
      setNotification({
        open: true,
        message: getErrorMessage(err, "No se pudo eliminar el documento"),
        isError: true,
      });
    } finally {
      setIsDeleting(false);
      setDocumentToDelete(null);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Nombre",
      render: (doc) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {doc.name}
        </Typography>
      ),
    },
    {
      key: "url",
      header: "Enlace",
      render: (doc) => (
        <Box
          component="a"
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            maxWidth: 360,
            color: theme.palette.primary.main,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {doc.url}
          </Box>
          <ExternalLink size={14} style={{ flexShrink: 0 }} />
        </Box>
      ),
    },
    {
      key: "created_by_name",
      header: "Añadido por",
      render: (doc) => doc.created_by_name || "—",
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (doc) => (
        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => setFormState({ open: true, document: doc })}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" onClick={() => setDocumentToDelete(doc)}>
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={documents}
        getRowId={(doc) => doc.id}
        loading={isLoading}
        emptyIcon={BookOpen}
        emptyMessage={search ? "No hay documentos que coincidan" : "Aún no hay documentación"}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar documento..."
      />

      <DocumentFormModal
        open={formState.open}
        document={formState.document}
        onClose={() => setFormState({ open: false, document: null })}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar documento"
        description={<>¿Seguro que quieres eliminar «{documentToDelete?.name}»?</>}
        confirmLabel="Eliminar"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />

      <SuccessPopup
        message={notification.message}
        isOpen={notification.open}
        onClose={() => setNotification({ open: false, message: "", isError: false })}
        error={notification.isError}
      />
    </>
  );
}
